import { useState, useEffect } from 'react';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSend, FiCheckCircle, FiCalendar } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

const RATING_OPTIONS = {
  excellent: '우수',
  good: '보통',
  needsImprovement: '노력 필요'
};

export default function WeeklyReports() {
  const { user } = useAuth();
  const isStaff = user?.role === 'admin' || user?.role === 'teacher';
  const [reports, setReports] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [formData, setFormData] = useState({
    studentId: '',
    startDate: '',
    endDate: '',
    progressSummary: '',
    overallRating: '',
    teacherComment: ''
  });

  useEffect(() => {
    loadData();
    if (isStaff) {
      loadStudents();
    }
  }, [isStaff]);

  const loadData = async () => {
    try {
      const res = await api.get('/api/weekly-reports');
      setReports(res.data.reports || []);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      const res = await api.get('/api/users?role=student&isActive=true');
      setStudents(res.data.users || []);
    } catch (error) {
      console.error('Failed to load students:', error);
    }
  };

  const openCreateModal = () => {
    // 기본값: 이번 주 월요일 ~ 금요일
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // 월요일
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 }); // 일요일
    weekEnd.setDate(weekEnd.getDate() - 2); // 금요일로 조정

    setFormData({
      studentId: '',
      startDate: format(weekStart, 'yyyy-MM-dd'),
      endDate: format(weekEnd, 'yyyy-MM-dd'),
      progressSummary: '',
      overallRating: 'good',
      teacherComment: ''
    });
    setSelectedReport(null);
    setShowModal(true);
  };

  const openEditModal = (report) => {
    if (report.notificationSent) {
      alert('이미 발송된 리포트는 수정할 수 없습니다');
      return;
    }
    setSelectedReport(report);
    setFormData({
      studentId: report.studentId._id,
      startDate: format(new Date(report.startDate), 'yyyy-MM-dd'),
      endDate: format(new Date(report.endDate), 'yyyy-MM-dd'),
      progressSummary: report.content.progressSummary || '',
      overallRating: report.content.overallRating || '',
      teacherComment: report.content.teacherComment || ''
    });
    setShowModal(true);
  };

  const openDetail = (report) => {
    setSelectedReport(report);
    setShowDetailModal(true);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();

    if (!formData.studentId || !formData.startDate || !formData.endDate) {
      alert('학생과 기간을 선택해주세요');
      return;
    }

    try {
      // 1. 리포트 생성 (자동 집계)
      const generateRes = await api.post('/api/weekly-reports/generate', {
        studentId: formData.studentId,
        startDate: formData.startDate,
        endDate: formData.endDate
      });

      const report = generateRes.data.report;

      // 2. 코멘트 등 업데이트
      if (formData.progressSummary || formData.overallRating || formData.teacherComment) {
        await api.put(`/api/weekly-reports/${report._id}`, {
          progressSummary: formData.progressSummary,
          overallRating: formData.overallRating,
          teacherComment: formData.teacherComment
        });
      }

      alert('주간 리포트가 생성되었습니다');
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '리포트 생성에 실패했습니다');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!selectedReport) return;

    try {
      await api.put(`/api/weekly-reports/${selectedReport._id}`, {
        progressSummary: formData.progressSummary,
        overallRating: formData.overallRating,
        teacherComment: formData.teacherComment
      });

      alert('수정되었습니다');
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '수정에 실패했습니다');
    }
  };

  const handleDelete = async (report) => {
    if (!window.confirm('이 리포트를 삭제하시겠습니까?')) return;

    try {
      await api.delete(`/api/weekly-reports/${report._id}`);
      alert('삭제되었습니다');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '삭제에 실패했습니다');
    }
  };

  const handleSend = async (report) => {
    if (!window.confirm('학부모에게 주간 리포트를 발송하시겠습니까?')) return;

    try {
      const res = await api.post(`/api/weekly-reports/${report._id}/send`);
      alert(`알림 발송 완료\n사용 포인트: ${res.data.result.pointsUsed}P`);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '발송에 실패했습니다');
    }
  };

  const stats = {
    total: reports.length,
    sent: reports.filter(r => r.notificationSent).length,
    pending: reports.filter(r => !r.notificationSent).length
  };

  return (
    <div className="weekly-reports-page">
      <div className="page-header">
        <h2>주간 리포트</h2>
        {isStaff && (
          <button className="btn btn-primary" onClick={openCreateModal}>
            <FiPlus /> 리포트 생성
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">전체 리포트</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.sent}</div>
          <div className="stat-label">발송 완료</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">대기 중</div>
        </div>
      </div>

      {/* Reports List */}
      <div className="reports-list">
        {loading ? (
          <div className="loading">로딩 중...</div>
        ) : reports.length === 0 ? (
          <div className="empty">주간 리포트가 없습니다</div>
        ) : (
          reports.map(report => {
            const period = `${format(new Date(report.startDate), 'MM/dd')}-${format(new Date(report.endDate), 'MM/dd')}`;
            return (
              <div key={report._id} className={`report-item ${report.notificationSent ? 'sent' : ''}`}>
                <div className="report-header">
                  <div>
                    <h3>{report.studentId?.name} 학생</h3>
                    <span className="report-period">
                      <FiCalendar /> {period}
                    </span>
                  </div>
                  {report.notificationSent && (
                    <span className="badge badge-success">
                      <FiCheckCircle /> 발송 완료
                    </span>
                  )}
                </div>
                <div className="report-content" onClick={() => openDetail(report)}>
                  <div className="report-stats">
                    <span>출석: {report.stats.attendedLessons}/{report.stats.totalLessons}회</span>
                    {report.content.overallRating && (
                      <span>평가: {RATING_OPTIONS[report.content.overallRating]}</span>
                    )}
                  </div>
                  {report.content.teacherComment && (
                    <p className="report-comment">{report.content.teacherComment.substring(0, 50)}{report.content.teacherComment.length > 50 ? '...' : ''}</p>
                  )}
                  <div className="report-meta">
                    <span>담임: {report.teacher?.name}</span>
                    <span>{format(new Date(report.createdAt), 'yyyy-MM-dd HH:mm')}</span>
                  </div>
                </div>
                {isStaff && (
                  <div className="report-actions">
                    {!report.notificationSent && (
                      <>
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => handleSend(report)}
                          title="알림 발송"
                        >
                          <FiSend /> 발송
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => openEditModal(report)}
                        >
                          <FiEdit2 /> 수정
                        </button>
                      </>
                    )}
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(report)}
                    >
                      <FiTrash2 /> 삭제
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedReport ? '리포트 수정' : '주간 리포트 생성'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={selectedReport ? handleUpdate : handleGenerate}>
              {!selectedReport && (
                <>
                  <div className="form-group">
                    <label>학생 선택 *</label>
                    <select
                      value={formData.studentId}
                      onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                      required
                    >
                      <option value="">학생을 선택하세요</option>
                      {students.map(student => (
                        <option key={student._id} value={student._id}>{student.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>시작일 *</label>
                      <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>종료일 *</label>
                      <input
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="form-group">
                <label>진도 요약</label>
                <textarea
                  value={formData.progressSummary}
                  onChange={(e) => setFormData({ ...formData, progressSummary: e.target.value })}
                  placeholder="예: 일차방정식 완료, 이차방정식 진행 중"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>종합 평가</label>
                <select
                  value={formData.overallRating}
                  onChange={(e) => setFormData({ ...formData, overallRating: e.target.value })}
                >
                  <option value="">선택 안 함</option>
                  {Object.entries(RATING_OPTIONS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>선생님 코멘트</label>
                <textarea
                  value={formData.teacherComment}
                  onChange={(e) => setFormData({ ...formData, teacherComment: e.target.value })}
                  placeholder="이번 주 학습 태도나 특이사항을 입력하세요"
                  rows="4"
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {selectedReport ? '수정' : '생성'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedReport && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>{selectedReport.studentId?.name} 학생 주간 리포트</h3>
                <p className="report-period">
                  {format(new Date(selectedReport.startDate), 'yyyy년 MM월 dd일')} ~ {format(new Date(selectedReport.endDate), 'MM월 dd일')}
                </p>
              </div>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="report-detail-section">
                <h4>📊 출석 현황</h4>
                <p>총 {selectedReport.stats.totalLessons}회 중 {selectedReport.stats.attendedLessons}회 참석</p>
              </div>

              {selectedReport.content.progressSummary && (
                <div className="report-detail-section">
                  <h4>📈 학습 진도</h4>
                  <p>{selectedReport.content.progressSummary}</p>
                </div>
              )}

              {selectedReport.content.overallRating && (
                <div className="report-detail-section">
                  <h4>⭐ 종합 평가</h4>
                  <p>{RATING_OPTIONS[selectedReport.content.overallRating]}</p>
                </div>
              )}

              {selectedReport.content.teacherComment && (
                <div className="report-detail-section">
                  <h4>💬 담임 선생님 코멘트</h4>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{selectedReport.content.teacherComment}</p>
                </div>
              )}

              <div className="report-detail-meta">
                <p>담임: {selectedReport.teacher?.name || '담임 선생님'}</p>
                <p>작성일: {format(new Date(selectedReport.createdAt), 'yyyy-MM-dd HH:mm')}</p>
                {selectedReport.notificationSent && (
                  <p>발송일: {format(new Date(selectedReport.notificationSentAt), 'yyyy-MM-dd HH:mm')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
